import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';

interface BusinessProfileData {
  headline: string;
  summary: string;
  aboutSection: string;
  serviceDescriptions: string[];
  keyFeatures: string[];
  callToAction: string;
  recommendedHashtags: string[];
}

const LinkedInBusinessProfileEnhancer: React.FC = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('');
  const [services, setServices] = useState<string[]>(['']);
  const [targetAudience, setTargetAudience] = useState('');
  const [uniqueSellingPoints, setUniqueSellingPoints] = useState<string[]>(['']);
  const [businessValues, setBusinessValues] = useState('');
  const [profileData, setProfileData] = useState<BusinessProfileData | null>(null);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const handleAddService = () => {
    setServices([...services, '']);
  };

  const handleServiceChange = (index: number, value: string) => {
    const updatedServices = [...services];
    updatedServices[index] = value;
    setServices(updatedServices);
  };

  const handleRemoveService = (index: number) => {
    if (services.length > 1) {
      const updatedServices = [...services];
      updatedServices.splice(index, 1);
      setServices(updatedServices);
    }
  };

  const handleAddSellingPoint = () => {
    setUniqueSellingPoints([...uniqueSellingPoints, '']);
  };

  const handleSellingPointChange = (index: number, value: string) => {
    const updatedPoints = [...uniqueSellingPoints];
    updatedPoints[index] = value;
    setUniqueSellingPoints(updatedPoints);
  };

  const handleRemoveSellingPoint = (index: number) => {
    if (uniqueSellingPoints.length > 1) {
      const updatedPoints = [...uniqueSellingPoints];
      updatedPoints.splice(index, 1);
      setUniqueSellingPoints(updatedPoints);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Filter out empty entries
    const filteredServices = services.filter(service => service.trim() !== '');
    const filteredSellingPoints = uniqueSellingPoints.filter(point => point.trim() !== '');
    
    if (!businessName || !industry || filteredServices.length === 0 || !targetAudience || filteredSellingPoints.length === 0 || !businessValues) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const userId = await getToken();
      const response = await fetch(
        `${ENV.API_URL}/api/marketing/linkedin-business-profile`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            userId,
            businessName, 
            industry, 
            services: filteredServices, 
            targetAudience, 
            uniqueSellingPoints: filteredSellingPoints, 
            businessValues 
          }),
        }
      );
      
      const data = await response.json();
      setProfileData(data.profileData);
      toast.success('LinkedIn business profile optimization complete!');
    } catch (error) {
      console.error('Error optimizing LinkedIn business profile:', error);
      toast.error('Failed to optimize LinkedIn business profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  // Example data for quick-fill
  const exampleData = {
    businessName: 'Acme Solutions',
    industry: 'Software Development',
    services: ['Custom SaaS Platforms', 'Cloud Migration', 'API Integrations'],
    targetAudience: 'Mid-sized businesses looking to modernize their operations',
    uniqueSellingPoints: ['Fast delivery', 'Enterprise security', 'Dedicated support'],
    businessValues: 'We believe in innovation, transparency, and helping clients scale efficiently.'
  };

  const handleUseExample = () => {
    setBusinessName(exampleData.businessName);
    setIndustry(exampleData.industry);
    setServices(exampleData.services);
    setTargetAudience(exampleData.targetAudience);
    setUniqueSellingPoints(exampleData.uniqueSellingPoints);
    setBusinessValues(exampleData.businessValues);
  };

  return (
    <div className="container mx-auto px-4 py-10 relative overflow-hidden">
      {/* Decorative blurred orbs */}
      <div className="absolute top-40 -left-24 w-72 h-72 bg-gradient-to-br from-cyan-200 to-blue-300 rounded-full opacity-20 blur-3xl"></div>
      <div className="absolute -top-20 right-10 w-96 h-96 bg-gradient-to-br from-blue-200 to-cyan-300 rounded-full opacity-20 blur-3xl"></div>
      
      <div className="relative z-10 mb-10">
        <h1 className="text-4xl font-extrabold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">LinkedIn Business Profile Enhancer</h1>
        <p className="text-gray-700 text-lg max-w-3xl">
          Optimize your company's LinkedIn profile with AI-generated professional content that attracts clients and showcases your business.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8">
          {/* Subtle gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-50/30 to-blue-50/30 opacity-70"></div>
          
          <div className="relative z-10">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600">Enhance Your Business Profile</h2>
              <button
                type="button"
                className="text-xs px-3 py-1.5 bg-white/40 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/60 transition-all duration-200 font-medium text-cyan-700 shadow-sm hover:shadow"
                onClick={handleUseExample}
              >
                <span className="flex items-center">
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Try Example
                </span>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="businessName" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 mb-2">
                Business Name <span className="text-cyan-500">*</span>
              </label>
              <input
                type="text"
                id="businessName"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-colors"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g. Acme Solutions"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="industry" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 mb-2">
                Industry <span className="text-cyan-500">*</span>
              </label>
              <input
                type="text"
                id="industry"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-colors"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g. Software Development, Marketing Agency"
                required
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 mb-2">
                Services Offered
              </label>
              {services.map((service, index) => (
                <div key={index} className="flex mb-3 last:mb-0 group">
                  <input
                    type="text"
                    className="flex-grow px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-l-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-colors"
                    value={service}
                    onChange={(e) => handleServiceChange(index, e.target.value)}
                    placeholder={`Service ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveService(index)}
                    className="bg-gradient-to-r from-red-400 to-red-500 text-white px-3 rounded-r-xl hover:from-red-500 hover:to-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 transition-all duration-200 shadow-sm group-hover:shadow"
                    disabled={services.length <= 1}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddService}
                className="mt-3 inline-flex items-center px-4 py-2 bg-white/40 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/60 transition-all duration-200 font-medium text-cyan-700 shadow-sm hover:shadow transform hover:-translate-y-0.5"
              >
                <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Service
              </button>
            </div>
            
            <div className="mb-6">
              <label htmlFor="targetAudience" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 mb-2">
                Target Audience
              </label>
              <textarea
                id="targetAudience"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-colors"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                placeholder="Describe your ideal clients or customers"
                rows={3}
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 mb-2">
                Unique Selling Points
              </label>
              {uniqueSellingPoints.map((point, index) => (
                <div key={index} className="flex mb-3 last:mb-0 group">
                  <input
                    type="text"
                    className="flex-grow px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-l-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-colors"
                    value={point}
                    onChange={(e) => handleSellingPointChange(index, e.target.value)}
                    placeholder={`Selling Point ${index + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveSellingPoint(index)}
                    className="bg-gradient-to-r from-red-400 to-red-500 text-white px-3 rounded-r-xl hover:from-red-500 hover:to-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 transition-all duration-200 shadow-sm group-hover:shadow"
                    disabled={uniqueSellingPoints.length <= 1}
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddSellingPoint}
                className="mt-3 inline-flex items-center px-4 py-2 bg-white/40 backdrop-blur-sm rounded-xl border border-white/30 hover:bg-white/60 transition-all duration-200 font-medium text-cyan-700 shadow-sm hover:shadow transform hover:-translate-y-0.5"
              >
                <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Selling Point
              </button>
            </div>
            
            <div className="mb-6">
              <label htmlFor="businessValues" className="block text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-600 mb-2">
                Business Values & Mission
              </label>
              <textarea
                id="businessValues"
                className="w-full px-4 py-3 bg-white/30 backdrop-blur-sm border border-white/40 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-colors"
                value={businessValues}
                onChange={(e) => setBusinessValues(e.target.value)}
                placeholder="Describe your company's values, mission, and vision"
                rows={4}
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-6 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 rounded-xl text-white font-medium shadow-lg hover:shadow-xl transform transition-all duration-300 hover:-translate-y-0.5 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Optimizing...
                </span>
              ) : 'Optimize LinkedIn Business Profile'}
            </button>
          </form>
          </div>
        </div>
        
        <div className="h-full">
          {profileData ? (
            <div className="relative overflow-hidden backdrop-blur-sm bg-white/20 border border-white/30 rounded-2xl shadow-xl p-8 h-full">
              {/* Subtle gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-cyan-50/30 opacity-70"></div>
              
              <div className="relative z-10">
                <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600 mb-6">Optimized LinkedIn Business Profile</h2>
                
                <div className="space-y-6">
                  <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
                    <h3 className="text-lg font-semibold text-blue-700 mb-2">Headline</h3>
                    <p className="text-gray-700 p-3 rounded-lg bg-white/50 backdrop-blur-sm border border-white/30">{profileData.headline}</p>
                  </div>
                  
                  <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
                    <h3 className="text-lg font-semibold text-blue-700 mb-2">Summary</h3>
                    <p className="text-gray-700 p-3 rounded-lg bg-white/50 backdrop-blur-sm border border-white/30 whitespace-pre-line">{profileData.summary}</p>
                  </div>
                  
                  <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
                    <h3 className="text-lg font-semibold text-blue-700 mb-2">About Section</h3>
                    <p className="text-gray-700 p-3 rounded-lg bg-white/50 backdrop-blur-sm border border-white/30 whitespace-pre-line">{profileData.aboutSection}</p>
                  </div>
                  
                  <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
                    <h3 className="text-lg font-semibold text-blue-700 mb-2">Service Descriptions</h3>
                    <div className="space-y-3">
                      {profileData.serviceDescriptions.map((desc, index) => (
                        <div key={index} className="p-3 rounded-lg bg-white/50 backdrop-blur-sm border border-white/30 shadow-sm">
                          <p className="text-gray-700 whitespace-pre-line">{desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
                    <h3 className="text-lg font-semibold text-blue-700 mb-2">Key Features & Benefits</h3>
                    <ul className="list-disc pl-5 space-y-1 p-3 rounded-lg bg-white/50 backdrop-blur-sm border border-white/30">
                      {profileData.keyFeatures.map((feature, index) => (
                        <li key={index} className="text-gray-700">{feature}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
                    <h3 className="text-lg font-semibold text-blue-700 mb-2">Call to Action</h3>
                    <p className="text-gray-700 p-3 rounded-lg bg-white/50 backdrop-blur-sm border border-white/30">{profileData.callToAction}</p>
                  </div>
                  
                  <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-white/40 shadow-sm transform transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
                    <h3 className="text-lg font-semibold text-blue-700 mb-2">Recommended Hashtags</h3>
                    <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-white/50 backdrop-blur-sm border border-white/30">
                      {profileData.recommendedHashtags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-sm"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center backdrop-blur-sm bg-white/10 border border-white/20 border-dashed rounded-2xl p-10 text-center">
              <div className="w-24 h-24 mb-6 rounded-full bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center opacity-80">
                <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600 mb-2">No Profile Optimization Yet</h3>
              <p className="text-gray-500 max-w-md">
                Fill out the form and click "Optimize LinkedIn Business Profile" to generate a professionally optimized profile for your business.
              </p>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-12 relative overflow-hidden backdrop-blur-sm bg-gradient-to-br from-blue-50/40 to-cyan-50/40 border border-white/30 rounded-2xl shadow-lg p-8">
        {/* Small animated orbs */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br from-blue-200 to-cyan-200 rounded-full opacity-20 blur-2xl animate-pulse"></div>
        <div className="absolute -bottom-12 -left-10 w-48 h-48 bg-gradient-to-br from-cyan-200 to-blue-200 rounded-full opacity-20 blur-2xl animate-pulse" style={{animationDelay: '1s'}}></div>
        
        <div className="relative z-10">
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600 mb-4">How It Works</h2>
          <p className="text-gray-700 mb-6 max-w-3xl">
            Our LinkedIn Business Profile Enhancer uses advanced AI to optimize your company's LinkedIn presence, making it more attractive to potential clients and partners.
          </p>
          
          <div className="bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-white/40 shadow-sm">
            <h3 className="text-xl font-semibold text-blue-700 mb-4">Benefits of an Optimized Business Profile</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                'Increased visibility in LinkedIn search results',
                'More effective communication of your value proposition',
                'Professional presentation of your services and expertise',
                'Better engagement with your target audience',
                'Enhanced credibility in your industry',
                'More qualified leads and business opportunities'
              ].map((benefit, index) => (
                <li key={index} className="flex items-start p-3 rounded-lg bg-white/60 backdrop-blur-sm border border-white/40 shadow-sm">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center mr-3 text-white shadow-sm">
                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                  <span className="text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedInBusinessProfileEnhancer;
